/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useState} from 'react'
import {Box} from '@mui/material'
import {useAppSelector} from 'src/hooks'
import {STATUS, familyTypes} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {dispatch} from 'src/redux-store'
import {restoreUserStatusPatch} from 'src/slices/userSlice'
import {Caregiver, Product, TimeslotProps} from 'src/types/types'
import Button from 'src/components-new/primitives/Button'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import TabBar from 'src/components-new/layout/TabBar'
import ActivityDetailDialogNew from 'src/components-new/feedback/ActivityDetailDialogNew'

import {buildFormValues, createPatchBody} from './profileFormUtils'
import UserDataSection from './sections/UserDataSection'
import DiseasesSection from './sections/DiseasesSection'
import ContactDataSection from './sections/ContactDataSection'
import ProgramMembershipSection from './sections/ProgramMembershipSection'
import OthersDataSection from './sections/OthersDataSection'
import FamilyCardSection from './sections/FamilyCardSection'
import PricingSection from './sections/PricingSection'
import ProfileActivitiesTab from './tabs/ProfileActivitiesTab'

const activitesStates = [
  {value: 'TO_BE_PLANNED', label: 'Da pianificare'},
  {value: 'TO_BE_CONFIRMED', label: 'Da confermare'},
  {value: 'TO_BE_CONCLUDED', label: 'Da concludere'},
  {value: 'TO_BE_VERIFIED', label: 'Da verificare'},
  {value: 'COMPLETED', label: 'Completata'},
  {value: 'CANCELLED', label: 'Cancellata'},
]

export default function ProfileNew(): JSX.Element {
  const {profileConfiguration} = useAppSelector(state => state.pspConfigReducer.data)
  const fields = profileConfiguration?.fields ?? {}
  const activitiesTypes = profileConfiguration?.activitiesTypes ?? []
  const activitiesColumnOptions = profileConfiguration?.activitiesColumnOptions ?? []

  const {data: userInfoData, statusPatch} = useAppSelector(({userReducer}) => userReducer)

  // Form state
  const [formValues, setFormValues] = useState<Record<string, any>>(() => buildFormValues(userInfoData ?? {}))
  const setField = (key: string, value: any) => setFormValues(prev => ({...prev, [key]: value}))

  useEffect(() => {
    setFormValues(buildFormValues(userInfoData ?? {}))
  }, [userInfoData])

  // Delegates & caregiver
  const [delegate1, setDelegate1] = useState<Record<string, any> | undefined>()
  const [delegate2, setDelegate2] = useState<Record<string, any> | undefined>()
  const [caregiver, setCaregiver] = useState<Record<string, any> | undefined>()
  const [timeslots, setTimeslots] = useState<TimeslotProps[]>(userInfoData?.preferredTimeSlots ?? [])

  // Family & products
  const [caregivers, setCaregivers] = useState<Caregiver[]>([])
  const [products, setProducts] = useState<Product[]>([])

  // Qualifications catalog (id -> name) to resolve operators' qualification ids
  const [qualificationsById, setQualificationsById] = useState<Record<string, string>>({})

  const toast = useToast()

  // Activities (DOCTOR only)
  const [activities, setActivities] = useState<Record<string, any>[]>([])
  const [activitiesCount, setActivitiesCount] = useState(0)
  const [activityFilters, setActivityFilters] = useState<any[]>([])
  const [activityPagination, setActivityPagination] = useState({page: 0, rowsPerPage: 10, order: ''})
  const [activityStatus, setActivityStatus] = useState<string | undefined>()
  const [selectedActivity, setSelectedActivity] = useState<any>()
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('patientData')

  const isDoctor = Boolean(userInfoData?.userGroup?.toUpperCase?.()?.includes?.('DOCTOR'))

  // Fetch family & products
  useEffect(() => {
    const fetchData = async () => {
      if (fields?.familyCard && !fields?.familyCard?.hidden) {
        const data = await apiClient.getPatientsCaregivers(userInfoData._id, () => {})
        setCaregivers(data)
      }
      if (fields?.ceilings && !fields?.ceilings?.hidden) {
        const data = await apiClient.getPatientProducts(userInfoData._id)
        setProducts(data)
      }
    }
    fetchData()
  }, [userInfoData._id, fields])

  // Fetch delegates (from doctors) & caregiver on mount
  useEffect(() => {
    const fetchCaregiverData = async () => {
      // Caregiver slot: still resolved from caregivers collection
      const caregivers = await apiClient.getCaregivers([], () => undefined)
      setCaregiver(caregivers.find((el: any) => el._id === userInfoData.caregiverId))

      // Delegate slots: Delegato 1/2 are, by product convention, the first and
      // second id of the allowed pool (delegateDoctorIds) — delegateId_1/2 are
      // never populated by any flow and are not used for matching. Delegates
      // are no longer editable from the profile, so only the (at most two)
      // ids actually shown need to be requested — not the whole pool.
      const [delegateDoctorId1, delegateDoctorId2] = (userInfoData.delegateDoctorIds ?? []).map(String)
      const requestedIds = [delegateDoctorId1, delegateDoctorId2].filter(Boolean)
      const candidates = requestedIds.length > 0
        ? await apiClient.getDoctorsSearch([{property: 'ids', value: requestedIds.join(',')}], () => undefined)
        : []
      setDelegate1((candidates ?? []).find((d: any) => String(d._id) === String(delegateDoctorId1)))
      setDelegate2(delegateDoctorId2 ? (candidates ?? []).find((d: any) => String(d._id) === String(delegateDoctorId2)) : undefined)
    }
    fetchCaregiverData()
  }, [userInfoData._id])

  // Sync timeslots when userInfoData changes
  useEffect(() => {
    setTimeslots(userInfoData?.preferredTimeSlots ?? [])
  }, [userInfoData._id])

  // Fetch qualifications catalog to resolve operators' qualification ids
  useEffect(() => {
    const fetchQualifications = async () => {
      const list = await apiClient.getQualifications(() => {}) ?? []
      setQualificationsById(Object.fromEntries(list.map((q: any) => [q._id, q.name])))
    }
    fetchQualifications()
  }, [])

  // Fetch activities (DOCTOR only)
  useEffect(() => {
    if (!isDoctor) return
    const fetchActivitiesData = async () => {
      const fetchedData = await apiClient.getActivities(activityFilters, setActivityStatus, activityPagination) ?? []
      const parsed = fetchedData.map((activity: any) => ({
        ...activity,
        stateReadable: activitesStates.find(s => s.value === activity.state)?.label,
        activityTypeReadable: activitiesTypes.find((t: any) => t.value === activity.typeCode)?.label,
      }))
      setActivities(parsed)
      const cnt = await apiClient.getActivitiesCount(activityFilters, () => {}) ?? 0
      setActivitiesCount(Number(cnt))
    }
    fetchActivitiesData()
  }, [activityFilters, activityPagination, userInfoData._id])

  // Watch statusPatch for feedback
  useEffect(() => {
    if (statusPatch === STATUS.FULFILLED) {
      toast.success('Modifica eseguita correttamente')
      dispatch(restoreUserStatusPatch())
    }
    if (statusPatch === STATUS.REJECTED) {
      toast.error('Errore nel modificare i dati')
      dispatch(restoreUserStatusPatch())
    }
  }, [statusPatch])

  const handleSave = async () => {
    const body = createPatchBody(formValues, caregiver, timeslots, fields)
    await apiClient.patchUserDataHttp(body)
  }

  const tabs = [
    {key: 'patientData', label: 'Anagrafica'},
    {key: 'activities', label: 'Attività'},
  ]

  const profileForm = (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
      <UserDataSection
        values={formValues}
        setField={setField}
        fields={fields}
        data={userInfoData}
        qualificationsById={qualificationsById}
      />
      <DiseasesSection fields={fields} data={userInfoData} />
      <ContactDataSection
        values={formValues}
        setField={setField}
        fields={fields}
        delegate1={delegate1}
        delegate2={delegate2}
        caregiver={caregiver}
        setCaregiver={setCaregiver}
        timeslots={timeslots}
        setTimeslots={setTimeslots}
      />
      {fields?.familyCard && !fields?.familyCard?.hidden && (
        <FamilyCardSection
          caregivers={caregivers.filter(c => Object.keys(familyTypes).includes(c.caregiverType))}
        />
      )}
      {fields?.ceilings && !fields?.ceilings?.hidden && (
        <PricingSection products={products} />
      )}
      <ProgramMembershipSection
        values={formValues}
        setField={setField}
        fields={fields}
      />
      <OthersDataSection
        values={formValues}
        setField={setField}
        fields={fields}
      />
      <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1}}>
        <Button
          variant="primary"
          onClick={handleSave}
          loading={statusPatch === STATUS.PENDING}
        >
          {'Salva'}
        </Button>
        <Button
          variant="secondary"
          onClick={() => setFormValues(buildFormValues(userInfoData ?? {}))}
        >
          {'Annulla'}
        </Button>
      </Box>
    </Box>
  )

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
      {activityStatus === STATUS.REJECTED && isDoctor && (
        <InlineMessage severity="error" onClose={() => setActivityStatus(undefined)}>
          {'Errore nel caricare le attività'}
        </InlineMessage>
      )}

      {isDoctor ? (
        <>
          <Box sx={{borderBottom: '1px solid', borderColor: 'divider'}}>
            <TabBar tabs={tabs} activeKey={activeTab} onChange={setActiveTab} />
          </Box>
          {activeTab === 'patientData' ? profileForm : (
            <ProfileActivitiesTab
              pagination={activityPagination}
              onPaginationChange={setActivityPagination}
              activities={activities}
              activitiesCount={activitiesCount}
              onFiltersSubmit={setActivityFilters}
              onActivityClick={(a) => { setSelectedActivity(a); setIsActivityModalOpen(true) }}
              loadStatus={activityStatus}
              columns={activitiesColumnOptions}
            />
          )}
          {selectedActivity && (
            <ActivityDetailDialogNew
              activity={selectedActivity}
              open={isActivityModalOpen}
              onClose={() => setIsActivityModalOpen(false)}
            />
          )}
        </>
      ) : profileForm}
    </Box>
  )
}
